import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ShoppingBag, X, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/track-order", label: "Track Order" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/faq", label: "FAQ" },
];

export const Navbar = () => {
  const { count } = useCart();
  const { logo } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-cream/90 backdrop-blur-xl border-b border-[#E5DFD3]" : "bg-cream/60 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <div className="flex items-center gap-3 md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" aria-label="Menu" className="p-2 -ml-2 text-choco">
                <Menu size={24} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-cream w-[80%] border-r border-[#E5DFD3]">
              <div className="mt-4 mb-8 flex items-center gap-3">
                <img src={logo} alt="Nel Bake House" className="h-12 w-12 rounded-full object-cover ring-1 ring-[#E5DFD3]" />
                <div>
                  <span className="font-display text-xl font-bold text-choco tracking-wide block leading-none">NEL BAKE HOUSE</span>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-goldsoft mt-1">Healthy & No Preservatives</p>
                </div>
              </div>
              <nav className="flex flex-col gap-1">
                {links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                    onClick={() => setOpen(false)}
                    className="py-3 text-lg text-chocodark border-b border-[#EFEBE1] hover:text-goldsoft transition-colors"
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 leading-none">
          <img src={logo} alt="Nel Bake House" className="h-11 w-11 rounded-full object-cover ring-1 ring-[#E5DFD3]" />
          <span className="hidden sm:flex flex-col">
            <span className="font-display text-lg sm:text-xl font-bold text-choco tracking-wide">NEL BAKE HOUSE</span>
            <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.28em] text-goldsoft">Healthy & No Preservatives</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className="text-sm font-medium text-chocodark hover:text-goldsoft transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-search-btn"
            onClick={() => navigate("/shop")}
            aria-label="Search"
            className="hidden sm:flex p-2 text-choco hover:text-goldsoft transition-colors"
          >
            <Search size={20} />
          </button>
          <Link
            to="/cart"
            data-testid="nav-cart-btn"
            aria-label="Cart"
            className="relative p-2 text-choco hover:text-goldsoft transition-colors"
          >
            <ShoppingBag size={22} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 bg-gold text-chocodark text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>
  );
};
